
import React, { useState, useEffect, useRef } from "react";
import AudioPlayer from "../common/AudioPlayer";
import NestedSection from "./accordion/NestedSection";
import axios from 'axios';
import WaveformPlayer from "../common/AudioPlayerTest";
import AudioPlayerTest from "../common/AudioPlayerTest";
import { PlusIcon, Menu, Settings } from "lucide-react";
import AppButton from "../common/AppButton";
import AddHeadingModal from "./heading/AddHeadingModal";
import EditHeadingModal from "./heading/EditHeadingModal";
import { useDispatch } from "react-redux";
import { editHeadingAsync, deleteHeadingAsync } from "../../redux/heading";
import { editParagraphAsync, deleteParagraphAsync } from "../../redux/paragraph";
import { getFullTranscriptionByIdAsync } from "../../redux/transcription";
import { pushUndo, buildUndoEntry, fetchSnapshots } from "../../redux/undo";
import NavigationBox from "./NavigationBox";
import { Tabs, Tab } from "@heroui/react";
import {
  HighlightsProvider,
  useHighlights,
} from "../../contexts/HighlightsContext";
import HighlightsPanel from "./HighlightsPanel";

const SessionView = ({
  data,
  filename,
  sessionId,
  setSelectedSpeakers,
  headings,
  speakers,
  searchTarget,
  searchJumpCount,
  expandAllToken,
  expandAll,
  navData,
  sessionStart,
  navRefresh,
  sessionName,
  filtersExpanded,
  onToggleFilters,
  filterSpeakers,
  selectedFilterSpeakers,
  onFilterSpeakersChange,
  headingOptions,
  selectedHeadingFilters,
  onHeadingFilterChange,
  selectedGroupByKeys,
  onGroupByChange,
  searchQuery,
  onSearchQueryChange,
  onSearchPrev,
  onSearchNext,
  searchPosition,
  onToggleSpeakersModal,
  onToggleAllSpeakers,
  areAllSpeakersOpen,
  onExportClick,
  isEmbedded = false,
  isActive = true,
  promptMode = "soft",
  onPromptModeChange,
  readOnly = false,
  lockReason = null,
}) => {
  const dispatch = useDispatch();
  const [audioFile, setAudioFile] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);  
  const [ correctedParagraphs, setCorrectedParagraphs] = useState({})
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditHeadingOpen, setIsEditHeadingOpen] = useState(false);
  const [selectedHeadingForEdit, setSelectedHeadingForEdit] = useState(null);
  const [activeParagraphId, setActiveParagraphId] = useState(null);
  const [isAddHeadingOpen, setIsAddHeadingOpen] = useState(false);
  const [addHeadingDefaults, setAddHeadingDefaults] = useState({ startSec: null, beforeId: null });

  // Read-only while the session is transcribed again (see Session.jsx): the
  // headings and the transcript take no clicks, keys or focus, and no dialog
  // that changes them stays open. `inert` is passed on as a plain attribute
  // by React 18, hence "" rather than true.
  const lockedProps = readOnly ? { inert: "", "aria-disabled": true } : {};
  useEffect(() => {
    if (!readOnly) return;
    setIsModalOpen(false);
    setIsAddHeadingOpen(false);
    setIsEditHeadingOpen(false);
  }, [readOnly]);
  const [waveHeadings, setWaveHeadings] = useState(Array.isArray(headings) ? headings : []);
  const [localData, setLocalData] = useState(Array.isArray(data) ? data : []);
  const [localNavData, setLocalNavData] = useState(Array.isArray(navData) ? navData : []);
  useEffect(() => {
    setWaveHeadings(Array.isArray(headings) ? headings : []);
  }, [headings]);
  useEffect(() => {
    setLocalData(Array.isArray(data) ? data : []);
  }, [data]);
  useEffect(() => {
    setLocalNavData(Array.isArray(navData) ? navData : []);
  }, [navData, navRefresh]);

  useEffect(() => {
    const fetchBlobUrl = async () => {
      try {
        const response = await axios.get(`transcriptions_poc_000113_1_0_0/transcription/get-url-mp3/${filename}`);
        
        // Axios automatically parses JSON responses
        if (response.data?.url) {
          setAudioUrl(response.data.url);
        }
      } catch (error) {
        if (error.response) {
          console.error('Server error:', error.response.status, error.response.data);
        } else if (error.request) {
          console.error('No response received:', error.request);
        } else {
          console.error('Error setting up request:', error.message);
        }
      }
    };
  
    if (filename) {
      fetchBlobUrl();
    }
  }, [filename]);

  const handleFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      setAudioFile(event.target.files[0]);
    }
  };

  // const sectionCount = 8;
  // const sectionRefs = useRef([]);
  const [openSectionKey, setOpenSectionKey] = useState([]);
  const [targetNested, setTargetNested] = useState(null);
  const [selectedSectionMap, setSelectedSectionMap] = useState(null);
  const [selectedNestedSectionMap, setSelectedNestedSectionMap] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [newTimeSetted, setNewTimeSetted] = useState(0);
  const [autoscrollEnabled, setAutoscrollEnabled] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const lastScrollTimeRef = useRef(0);
  const lastParagraphKeyRef = useRef(null);
  const initializedOpenSectionsRef = useRef(false);
  // When an external search selects a target paragraph, we scroll/open it
  const searchTargetRef = useRef(null);
  // Track last paragraph we jumped to for autoscroll
  const lastJumpedParagraphRef = useRef(null);


  const groupedSections = localData; // This gives you the structured heading2 → children format
  const flatChildren = React.useMemo(() => {
    if (!Array.isArray(groupedSections)) return [];
    const acc = [];
    for (const section of groupedSections) {
      if (Array.isArray(section?.children)) {
        acc.push(...section.children);
      }
    }
    return acc;
  }, [groupedSections]);
  const paragraphChildren = React.useMemo(
    () => flatChildren.filter((c) => c?.type === "paragraph"),
    [flatChildren]
  );
  const paragraphIdToRefIndex = React.useMemo(() => {
    const map = new Map();
    let counter = 0;
    for (let i = 0; i < flatChildren.length; i++) {
      const item = flatChildren[i];
      if (item?.type === "paragraph") {
        counter += 1;
        if (item?.id != null) {
          map.set(item.id, counter);
        }
      }
    }
    return map;
  }, [flatChildren]);
  const paragraphIdToStart = React.useMemo(() => {
    const map = new Map();
    for (let i = 0; i < paragraphChildren.length; i++) {
      const p = paragraphChildren[i];
      if (p?.id != null) map.set(p.id, p.start);
    }
    return map;
  }, [paragraphChildren]);

  // Store session data globally for Sitzung View to access
  useEffect(() => {
    if (sessionId && speakers && paragraphChildren) {
      // Initialize global store if needed
      if (!window.sessionDataStore) {
        window.sessionDataStore = new Map();
      }
      
      window.sessionDataStore.set(String(sessionId), {
        speakers,
        paragraphChildren
      });
    }
  }, [sessionId, speakers, paragraphChildren]);

  const groupingHeadingType = React.useMemo(
    () => getFirstFromCollection(selectedGroupByKeys),
    [selectedGroupByKeys]
  );
  const getHeadingStartByBeforeId = (beforeId) => {
    if (beforeId == null) return null;
    const key = Number(beforeId);
    // Coerce to number to match paragraph ids used as map keys
    return paragraphIdToStart.get(Number.isNaN(key) ? beforeId : key) ?? null;
  };
  // Helpers for ISO 8601 duration conversion (PT..S)
  const isoDurationToSeconds = (iso) => {
    if (!iso || typeof iso !== "string") return 0;
    const m = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?/);
    if (!m) return 0;
    const h = Number(m[1] || 0);
    const min = Number(m[2] || 0);
    const s = Number(m[3] || 0);
    return h * 3600 + min * 60 + s;
  };
  const secondsToIsoDuration = (sec) => {
    let remaining = Math.max(0, Number(sec) || 0);
    const h = Math.floor(remaining / 3600);
    remaining -= h * 3600;
    const m = Math.floor(remaining / 60);
    remaining -= m * 60;
    const s = Math.round((remaining + Number.EPSILON) * 100) / 100;
    const parts = [];
    if (h > 0) parts.push(`${h}H`);
    if (m > 0) parts.push(`${m}M`);
    parts.push(`${s}S`);
    return `PT${parts.join("")}`;
  };

  const secondsToCustomDateTime = (baseStartISO, offsetSeconds) => {
    if (!baseStartISO || typeof offsetSeconds !== "number") return null;
    const startDate = new Date(baseStartISO);
    const newDate = new Date(startDate.getTime() + offsetSeconds * 1000);
    const pad = (n) => n.toString().padStart(2, "0");
    const day = pad(newDate.getUTCDate());
    const month = pad(newDate.getUTCMonth() + 1);
    const year = newDate.getUTCFullYear();
    const hours = pad(newDate.getUTCHours());
    const minutes = pad(newDate.getUTCMinutes());
    const seconds = pad(newDate.getUTCSeconds());
    return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
  };
  const normalizeId = (value) => {
    if (value == null) return null;
    const asNumber = Number(value);
    return Number.isNaN(asNumber) ? value : asNumber;
  };
  function getFirstFromCollection(collection) {
    if (!collection) return null;
    if (collection instanceof Set) {
      const iterator = collection.values().next();
      return iterator.done ? null : iterator.value;
    }
    if (Array.isArray(collection)) {
      return collection.length > 0 ? collection[0] : null;
    }
    return collection;
  }
  const findParagraphLocationInSections = (sections, targetId) => {
    if (!Array.isArray(sections) || targetId == null) return null;
    const normalizedTarget = normalizeId(targetId);
    for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
      const children = Array.isArray(sections[sectionIndex]?.children)
        ? sections[sectionIndex].children
        : [];
      for (let childIndex = 0; childIndex < children.length; childIndex++) {
        const child = children[childIndex];
        if (child?.type === "paragraph" && normalizeId(child.id) === normalizedTarget) {
          return { sectionIndex, childIndex };
        }
      }
    }
    return null;
  };
  const findSectionIndexByStart = (sections, startSeconds) => {
    if (!Array.isArray(sections) || typeof startSeconds !== "number" || sections.length === 0) {
      return null;
    }
    for (let i = 0; i < sections.length; i++) {
      const currentStartRaw = Number(sections[i]?.heading2?.start);
      const currentStart = Number.isFinite(currentStartRaw) ? currentStartRaw : 0;
      const nextStartRaw = Number(sections[i + 1]?.heading2?.start);
      const nextStart = Number.isFinite(nextStartRaw) ? nextStartRaw : Infinity;
      if (startSeconds < currentStart) {
        return Math.max(i - 1, 0);
      }
      if (startSeconds >= currentStart && startSeconds < nextStart) {
        return i;
      }
    }
    return sections.length - 1;
  };
  const allWordOffsets = React.useMemo(() => {
    if (!Array.isArray(paragraphChildren) || paragraphChildren.length === 0) return [];
    const offsets = [];
    const seen = new Set();
    const pushOffset = (value) => {
      if (!Number.isFinite(value)) return;
      const key = value.toFixed(3);
      if (!seen.has(key)) {
        seen.add(key);
        offsets.push(value);
      }
    };
    for (const paragraph of paragraphChildren) {
      if (!paragraph) continue;
      const paragraphStart = Number(paragraph.start);
      const words = Array.isArray(paragraph?.paragraph_words) ? paragraph.paragraph_words : [];
      if (words.length > 0) {
        words.forEach((word) => {
          let offset = typeof word?.offsetSeconds === "number"
            ? word.offsetSeconds
            : isoDurationToSeconds(word?.word_offset);
          if (!Number.isFinite(offset) && Number.isFinite(paragraphStart)) {
            offset = paragraphStart;
          }
          pushOffset(offset);
        });
      } else if (Number.isFinite(paragraphStart)) {
        pushOffset(paragraphStart);
      }
    }
    offsets.sort((a, b) => a - b);
    return offsets;
  }, [paragraphChildren]);

  const handleCommitReorder = async (headingObj, targetBeforeParagraphId, startSeconds) => {
    try {
      const isoStart = secondsToCustomDateTime(sessionStart, startSeconds);
      await dispatch(editHeadingAsync({
        id: headingObj.id,
        payload: {
          before_paragraph_id: targetBeforeParagraphId,
          start: isoStart
        }
      }));
      // Optimistic: no refetch; NavigationBox updates its local state
      setWaveHeadings((prev) => {
        const list = Array.isArray(prev) ? prev.slice() : [];
        const idx = list.findIndex((h) => h.id === headingObj.id);
        if (idx !== -1) {
          list[idx] = { ...list[idx], start: startSeconds };
        } else {
          // if not present, append minimal info
          list.push({ id: headingObj.id, start: startSeconds });
        }
        list.sort((a, b) => (a?.start ?? 0) - (b?.start ?? 0));
        return list;
      });
    } catch (e) {
      console.error("Failed to update heading position", e);
    }
  };

  const handleDeleteHeading = async (heading) => {
    try {
      await dispatch(deleteHeadingAsync(heading.id));
      // no refetch; NavigationBox handles optimistic removal
    } catch (e) {
      console.error("Failed to delete heading", e);
    }
  };

  const updateHeadingContentInSections = (sections, headingId, content) => {
    if (!Array.isArray(sections)) return sections;
    return sections.map((section) => {
      const updateHeading = (heading) => {
        if (heading && heading.id === headingId) {
          return { ...heading, content };
        }
        return heading;
      };
      return {
        ...section,
        heading2: updateHeading(section.heading2),
        children: (section.children || []).map((child) =>
          child?.type === "paragraph" ? child : updateHeading(child)
        ),
      };
    });
  };

  const handleQuickHeadingRename = async (headingObj, nextTitle) => {
    if (!headingObj?.id) return;
    const trimmed = (nextTitle || "").trim();
    if (!trimmed) return;
    try {
      await dispatch(
        editHeadingAsync({
          id: headingObj.id,
          payload: { content: trimmed },
        })
      );
      setLocalData((prev) => updateHeadingContentInSections(prev, headingObj.id, trimmed));
      setLocalNavData((prev) => updateHeadingContentInSections(prev, headingObj.id, trimmed));
    } catch (e) {
      console.error("Failed to rename heading", e);
    }
  };
  const sectionCount = groupedSections.length;
  const sectionRefs = useRef([]);
  const scrollContainerRef = useRef(null);
  const scrollAnimationFrameRef = useRef(null);
  if (sectionRefs.current.length !== sectionCount) {
    sectionRefs.current = Array(sectionCount)
      .fill()
      .map((_, i) => sectionRefs.current[i] || React.createRef());
  }

  // Create refs for sections
  if (sectionRefs.current.length !== sectionCount) {
    sectionRefs.current = Array(sectionCount)
      .fill()
      .map((_, i) => sectionRefs.current[i] || React.createRef());
  }

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const animateContainerScroll = React.useCallback((container, targetTop, durationMs = 300) => {
    if (!container) return;
    const startTop = container.scrollTop;
    const delta = targetTop - startTop;
    if (Math.abs(delta) < 1) return;

    if (scrollAnimationFrameRef.current) {
      cancelAnimationFrame(scrollAnimationFrameRef.current);
      scrollAnimationFrameRef.current = null;
    }

    const startTime = performance.now();
    const easeInOutSine = (t) => -(Math.cos(Math.PI * t) - 1) / 2;

    const step = (now) => {
      const progress = Math.min((now - startTime) / durationMs, 1);
      const eased = easeInOutSine(progress);
      container.scrollTop = startTop + delta * eased;
      if (progress < 1) {
        scrollAnimationFrameRef.current = requestAnimationFrame(step);
      } else {
        scrollAnimationFrameRef.current = null;
      }
    };

    scrollAnimationFrameRef.current = requestAnimationFrame(step);
  }, []);

  useEffect(() => {
    return () => {
      if (scrollAnimationFrameRef.current) {
        cancelAnimationFrame(scrollAnimationFrameRef.current);
      }
    };
  }, []);

  // Find the active paragraph across all sections, accounting for boundaries
  const findActiveParagraph = React.useCallback((timeSeconds) => {
    let fallbackLast = null;
    for (let s = 0; s < groupedSections.length; s++) {
      const children = groupedSections[s]?.children || [];
      const paragraphChildren = children.filter((c) => c.type === "paragraph");
      if (!paragraphChildren.length) continue;

      for (let i = 0; i < paragraphChildren.length; i++) {
        const currentStart = paragraphChildren[i].start;
        let nextStart;
        if (i + 1 < paragraphChildren.length) {
          nextStart = paragraphChildren[i + 1].start;
        } else {
          // boundary: use next section's first paragraph start if available
          let nextSectionFirstParagraphStart = undefined;
          for (let ns = s + 1; ns < groupedSections.length; ns++) {
            const nextParas = (groupedSections[ns]?.children || []).filter((c) => c.type === "paragraph");
            if (nextParas.length) {
              nextSectionFirstParagraphStart = nextParas[0].start;
              break;
            }
          }
          nextStart = nextSectionFirstParagraphStart ?? Infinity;
        }

        if (timeSeconds >= currentStart && timeSeconds < nextStart) {
          return { sectionIndex: s, paragraphIndex: i };
        }

        if (timeSeconds >= currentStart) {
          fallbackLast = { sectionIndex: s, paragraphIndex: i };
        }
      }
    }
    return fallbackLast; // last paragraph before time
  }, [groupedSections]);

  const handleScrollToNested = React.useCallback(async (sectionIndex, paragraphRefIndex, positionAtTop = false) => {
    const sectionRef = sectionRefs.current[sectionIndex];
    const sectionKey = `section-${sectionIndex}`;
    const nestedDomId = `section-${sectionIndex}-nested-${paragraphRefIndex}`;

    setSelectedNestedSectionMap(`section-${sectionIndex}-nested-${paragraphRefIndex}`);
    setSelectedSectionMap(sectionKey);
    await delay(160);

    if (sectionRef?.current) {
      setOpenSectionKey((prev) => {
        const next = new Set(prev || []);
        next.add(sectionKey);
        return Array.from(next);
      });
      setTargetNested(paragraphRefIndex);
    }

    // Light scroll routine: wait briefly for target mount, then animate once.
    const container = scrollContainerRef.current;
    const maxAttempts = 8;
    const tickInterval = 60;
    const attemptScroll = (attempt = 0) => {
      const el = document.getElementById(nestedDomId);
      if (!container || !el || el.getBoundingClientRect().height <= 0) {
        if (attempt < maxAttempts) setTimeout(() => attemptScroll(attempt + 1), tickInterval);
        return;
      }
      const containerRect = container.getBoundingClientRect();
      const rect = el.getBoundingClientRect();
      const offset = positionAtTop ? 20 : 100;
      const targetTop = Math.max(rect.top - containerRect.top + container.scrollTop - offset, 0);
      animateContainerScroll(container, targetTop, positionAtTop ? 1300 : 850);
    };
    requestAnimationFrame(() => attemptScroll(0));
  }, [animateContainerScroll]);

  function countParagraphsUpToIndex(children, i) {
    // console.log(children)
    // console.log(i)
    if (!Array.isArray(children) || i < 0 || i >= children.length) return 0;
  
    let count = 0;
    for (let j = 0; j <= i; j++) {
      if (children[j].type === "paragraph") {
        count++;
      }
      // console.log("count", count)
    }
    return count;
  }

  useEffect(() => {
    if (!groupedSections.length) return;
    
    // Paragraph-based match across all sections
    const match = findActiveParagraph(currentTime);
    if (!match) return;

    const newSectionKey = `section-${match.sectionIndex}`;
    const paragraphRefIndex = match.paragraphIndex + 1; // 1-based index for nested keys
    const newNestedKey = `section-${match.sectionIndex}-nested-${paragraphRefIndex}`;
    
    // Track active paragraph id for highlighting in navigation (always do this)
    try {
      const children = (groupedSections[match.sectionIndex]?.children || []).filter((c) => c.type === "paragraph");
      const activePara = children?.[match.paragraphIndex];
      if (activePara?.id != null) {
        setActiveParagraphId(activePara.id);
      }
    } catch {}

    // Track paragraph changes
    const paragraphChanged = lastParagraphKeyRef.current !== newNestedKey;
    lastParagraphKeyRef.current = newNestedKey;
    
    // Heading-based section (for keeping both open if boundary case)
    let headingSectionIndex = groupedSections.findIndex((section, i) => {
      const currentStart = section.heading2.start;
      const nextStart = groupedSections[i + 1]?.heading2.start ?? Infinity;
      return currentTime >= currentStart && currentTime < nextStart;
    });
    if (headingSectionIndex === -1) {
      if (currentTime < groupedSections[0]?.heading2?.start) headingSectionIndex = 0;
      else headingSectionIndex = groupedSections.length - 1;
    }
    
    // Always update selection state so sections stay highlighted and open
    if (selectedNestedSectionMap !== newNestedKey) {
      setSelectedNestedSectionMap(newNestedKey);
    }
    if (selectedSectionMap !== newSectionKey) {
      setSelectedSectionMap(newSectionKey);
    }
    
    // Always ensure current sections are open
    const headingSectionKey = `section-${headingSectionIndex}`;
    setOpenSectionKey((prev) => {
      const next = new Set(prev || []);
      const prevSize = next.size;
      next.add(newSectionKey);
      if (headingSectionKey !== newSectionKey) {
        next.add(headingSectionKey);
      }
      if (next.size === prevSize) {
        return prev;
      }
      return Array.from(next);
    });
    
    // Always ensure sections are open and update selection
    // When autoscroll is OFF, also jump to paragraph on change
    if (!autoscrollEnabled || !isPlaying) {
      if (paragraphChanged) {
        handleScrollToNested(match.sectionIndex, paragraphRefIndex);
      }
    } else if (autoscrollEnabled && isPlaying && paragraphChanged) {
      // When autoscroll is ON, jump to the new speaker, then continue slow scroll
      handleScrollToNested(match.sectionIndex, paragraphRefIndex);
    }
  }, [currentTime, autoscrollEnabled, isPlaying]);

  // Calculate paragraph end time based on last word timestamp
  const getParagraphEndTime = React.useCallback((paragraph) => {
    if (!paragraph) return null;
    
    // Get the last word's timestamp from paragraph_words
    const words = Array.isArray(paragraph?.paragraph_words) ? paragraph.paragraph_words : [];
    if (words.length > 0) {
      // Find the word with the latest offsetSeconds
      let lastWordTime = null;
      for (const word of words) {
        const offset = typeof word?.offsetSeconds === "number"
          ? word.offsetSeconds
          : isoDurationToSeconds(word?.word_offset);
        if (Number.isFinite(offset)) {
          if (lastWordTime == null || offset > lastWordTime) {
            lastWordTime = offset;
          }
        }
      }
      // Add a small buffer (0.5 seconds) after the last word
      if (lastWordTime != null) {
        return lastWordTime + 0.5;
      }
    }
    
    // Fallback: use paragraph end if available
    const paragraphStart = Number(paragraph.start);
    const paragraphEnd = isoDurationToSeconds(paragraph.end);
    if (Number.isFinite(paragraphEnd) && paragraphEnd > 0) {
      return paragraphStart + paragraphEnd;
    }
    
    // If no words or end time, return null (will use next paragraph start as boundary)
    return null;
  }, []);

  // Jump-based autoscroll: jump to next paragraph when current one ends
  useEffect(() => {
    if (!autoscrollEnabled || !isPlaying) {
      lastJumpedParagraphRef.current = null;
      return;
    }
    if (!groupedSections.length) return;
    
    // Find current active paragraph
    const match = findActiveParagraph(currentTime);
    if (!match) return;
    
    const children = (groupedSections[match.sectionIndex]?.children || []).filter((c) => c.type === "paragraph");
    const currentParagraph = children?.[match.paragraphIndex];
    if (!currentParagraph) return;
    
    // Calculate when this paragraph ends
    const paragraphEndTime = getParagraphEndTime(currentParagraph);
    
    // Find next paragraph start time
    let nextParagraphStart = null;
    if (match.paragraphIndex + 1 < children.length) {
      nextParagraphStart = Number(children[match.paragraphIndex + 1].start);
    } else {
      // Look in next section
      for (let ns = match.sectionIndex + 1; ns < groupedSections.length; ns++) {
        const nextParas = (groupedSections[ns]?.children || []).filter((c) => c.type === "paragraph");
        if (nextParas.length) {
          nextParagraphStart = Number(nextParas[0].start);
          break;
        }
      }
    }
    
    // Determine paragraph end: use word-based end if available, otherwise use next paragraph start
    const effectiveEndTime = paragraphEndTime != null 
      ? (nextParagraphStart != null ? Math.min(paragraphEndTime, nextParagraphStart) : paragraphEndTime)
      : nextParagraphStart;
    
    if (effectiveEndTime == null) return;
    
    // Check if we've passed the paragraph end time
    const hasPassedEnd = currentTime >= effectiveEndTime;
    const currentParagraphKey = `section-${match.sectionIndex}-nested-${match.paragraphIndex + 1}`;
    
    // Only jump if we've passed the end and haven't already jumped for this transition
    if (hasPassedEnd && lastJumpedParagraphRef.current !== currentParagraphKey) {
      // Find next paragraph
      let nextSectionIndex = match.sectionIndex;
      let nextParagraphIndex = match.paragraphIndex + 1;
      
      if (nextParagraphIndex >= children.length) {
        // Move to next section
        nextSectionIndex = match.sectionIndex + 1;
        nextParagraphIndex = 0;
        
        // Find first paragraph in next section
        while (nextSectionIndex < groupedSections.length) {
          const nextSectionChildren = (groupedSections[nextSectionIndex]?.children || []).filter((c) => c.type === "paragraph");
          if (nextSectionChildren.length > 0) {
            break;
          }
          nextSectionIndex++;
        }
      }
      
      // Only jump if there's a next paragraph
      if (nextSectionIndex < groupedSections.length) {
        const nextSectionChildren = (groupedSections[nextSectionIndex]?.children || []).filter((c) => c.type === "paragraph");
        if (nextParagraphIndex < nextSectionChildren.length) {
          const nextParagraphRefIndex = nextParagraphIndex + 1; // 1-based
          lastJumpedParagraphRef.current = `section-${nextSectionIndex}-nested-${nextParagraphRefIndex}`;
          
          // Add a small delay before jumping to slow down autoscroll
          setTimeout(() => {
            // Jump to next paragraph, positioning it at the top
            handleScrollToNested(nextSectionIndex, nextParagraphRefIndex, true);
          }, 200);
        }
      }
    } else if (!hasPassedEnd) {
      // Reset jump tracking if we're still within the paragraph
      lastJumpedParagraphRef.current = null;
    }
  }, [currentTime, autoscrollEnabled, isPlaying, groupedSections, getParagraphEndTime, findActiveParagraph, handleScrollToNested]);

  // Handle external search target jump
  useEffect(() => {
    if (!searchTarget) return;
    const { sectionIndex, paragraphRefIndex, jumpSeconds } = searchTarget || {};
    if (
      typeof sectionIndex === "number" &&
      typeof paragraphRefIndex === "number" &&
      sectionIndex >= 0 &&
      paragraphRefIndex >= 1
    ) {
      handleScrollToNested(sectionIndex, paragraphRefIndex);
      if (Number.isFinite(jumpSeconds)) {
        setNewTimeSetted(jumpSeconds);
      } else {
        const section = groupedSections?.[sectionIndex];
        const paragraphs = Array.isArray(section?.children)
          ? section.children.filter((c) => c.type === "paragraph")
          : [];
        const targetParagraph = paragraphs[paragraphRefIndex - 1];
        if (targetParagraph?.start != null) {
          setNewTimeSetted(Number(targetParagraph.start) || 0);
        }
      }
    }
  }, [searchJumpCount]);

  // Open all top-level sections on initial mount so nested entries are visible
  useEffect(() => {
    if (!initializedOpenSectionsRef.current && Array.isArray(groupedSections) && groupedSections.length > 0) {
      const allKeys = groupedSections.map((_, idx) => `section-${idx}`);
      setOpenSectionKey(allKeys);
      initializedOpenSectionsRef.current = true;
    }
  }, [groupedSections]);

  const handleHeadingCreated = React.useCallback(
    ({ heading, startSeconds, beforeParagraphId }) => {
      if (!heading) return;
      const normalizedStartSeconds =
        typeof startSeconds === "number" && Number.isFinite(startSeconds)
          ? startSeconds
          : null;
      const normalizedBeforeId =
        beforeParagraphId != null ? normalizeId(beforeParagraphId) : null;

      setWaveHeadings((prev) => {
        const list = Array.isArray(prev) ? prev.slice() : [];
        const filtered = list.filter((h) => h?.id !== heading.id);
        filtered.push({ id: heading.id, start: normalizedStartSeconds ?? 0 });
        filtered.sort((a, b) => (a?.start ?? 0) - (b?.start ?? 0));
        return filtered;
      });

      setLocalNavData((prev) => {
        if (!Array.isArray(prev) || prev.length === 0) {
          if (groupingHeadingType && heading.type === groupingHeadingType) {
            return [{ heading2: heading, children: [] }];
          }
          return prev;
        }
        const location =
          normalizedBeforeId != null
            ? findParagraphLocationInSections(prev, normalizedBeforeId)
            : null;

        if (location) {
          if (groupingHeadingType && heading.type === groupingHeadingType) {
            const updatedSections = [];
            prev.forEach((section, idx) => {
              if (idx !== location.sectionIndex) {
                updatedSections.push(section);
                return;
              }
              const {
                heading2: _originalHeading,
                children: originalChildren = [],
                ...sectionRest
              } = section;
              const beforeChildren = originalChildren.slice(0, location.childIndex);
              const afterChildren = originalChildren.slice(location.childIndex);
              updatedSections.push({ ...section, children: beforeChildren });
              updatedSections.push({ ...sectionRest, heading2: heading, children: afterChildren });
            });
            return updatedSections;
          }
          return prev.map((section, idx) => {
            if (idx !== location.sectionIndex) return section;
            const children = Array.isArray(section.children) ? section.children.slice() : [];
            const insertIdx = Math.min(Math.max(location.childIndex, 0), children.length);
            children.splice(insertIdx, 0, heading);
            return { ...section, children };
          });
        }

        const fallbackSectionIndex =
          normalizedStartSeconds != null
            ? findSectionIndexByStart(prev, normalizedStartSeconds)
            : null;

        if (groupingHeadingType && heading.type === groupingHeadingType) {
          if (fallbackSectionIndex == null) {
            return [...prev, { heading2: heading, children: [] }];
          }
          const nextSections = [];
          prev.forEach((section, idx) => {
            nextSections.push(section);
            if (idx === fallbackSectionIndex) {
              nextSections.push({ heading2: heading, children: [] });
            }
          });
          if (fallbackSectionIndex >= prev.length) {
            nextSections.push({ heading2: heading, children: [] });
          }
          return nextSections;
        }

        if (fallbackSectionIndex != null && fallbackSectionIndex >= 0) {
          return prev.map((section, idx) => {
            if (idx !== fallbackSectionIndex) return section;
            const children = Array.isArray(section.children) ? section.children.slice() : [];
            children.push(heading);
            return { ...section, children };
          });
        }

        return prev;
      });
    },
    [groupingHeadingType]
  );

  const handleOpenAddHeading = () => {
    let beforeId = null;
    let startSec = 0;
    const paraList = paragraphChildren;
    if (Array.isArray(paraList) && paraList.length > 0) {
      let targetParagraph = null;
      if (activeParagraphId != null) {
        targetParagraph = paraList.find((p) => p?.id === activeParagraphId) ?? null;
      }
      if (!targetParagraph) {
        const match = findActiveParagraph(currentTime);
        if (
          match &&
          match.paragraphIndex != null &&
          match.paragraphIndex >= 0 &&
          match.paragraphIndex < paraList.length
        ) {
          targetParagraph = paraList[match.paragraphIndex] ?? null;
        }
      }
      if (!targetParagraph) {
        const upcoming = paraList.find((p) => typeof p.start === "number" && p.start >= currentTime);
        targetParagraph = upcoming || paraList[0] || null;
      }
      beforeId = targetParagraph?.id ?? null;
      startSec = Number(targetParagraph?.start) || 0;
    }
    setAddHeadingDefaults({ startSec, beforeId });
    setIsAddHeadingOpen(true);
  };

  // Open/close all sections (and nested speaker items) when requested
  useEffect(() => {
    if (!expandAllToken) return;
    if (expandAll) {
      const allKeys = Array.from({ length: sectionCount }, (_, i) => `section-${i}`);
      setOpenSectionKey(allKeys);
    } else {
      setOpenSectionKey([]);
      setSelectedSectionMap(null);
      setSelectedNestedSectionMap(null);
    }
    setTargetNested(null);
  }, [expandAllToken, expandAll, sectionCount]);


    return (
      <HighlightsProvider transcriptionId={sessionId}>
      <div className={`flex flex-col ${isEmbedded ? "h-full" : "h-[88vh]"} relative`}> {/* Full height layout */}

        {/* Settings tab is now handled by Multiscreen component in the main navbar */}

        {/* Sticky Audio Player */}
        <div className="sticky top-0 z-30 rounded-2xl bg-surfaceContainer mb-1 pb-1">
          <div className="flex items-center gap-3">
            <AppButton
              onClick={() => window.dispatchEvent(new Event("open-drawer"))}
              width="w-10"
              roundedFull="rounded-full"
              className="p-2"
              aria-label="Open navigation menu"
            >
              <Menu className="w-5 h-5" />
            </AppButton>
            <div className="flex-1">
              {audioUrl && (
                <HighlightAwareAudioPlayer
                  sessionId={sessionId}
                  filename={filename}
                  currentTime={currentTime}
                  setCurrentTime={setCurrentTime}
                  duration={duration}
                  setDuration={setDuration}
                  newTimeSetted={newTimeSetted}
                  headings={waveHeadings}
                  wordOffsets={allWordOffsets}
                  paragraphs={paragraphChildren}
                  searchQuery={searchQuery}
                  onSearchQueryChange={onSearchQueryChange}
                  onSearchPrev={onSearchPrev}
                  onSearchNext={onSearchNext}
                  searchPosition={searchPosition}
                  onExportClick={onExportClick}
                  autoscrollEnabled={autoscrollEnabled}
                  onAutoscrollChange={setAutoscrollEnabled}
                  onPlayingStateChange={setIsPlaying}
                  isActive={isActive}
                  promptMode={promptMode}
                  onPromptModeChange={onPromptModeChange}
                  onToggleSpeakersModal={onToggleSpeakersModal}
                  onHighlightJumpTo={(h) =>
                    setNewTimeSetted(Number(h.start_offset))
                  }
                />
              )}
            </div>
          </div>
          {/* Highlights overview — collapsible, sits under the audio player so
              the user can scan/jump without scrolling the transcript. */}
          <div className="mt-1">
            <HighlightsPanel
              onJumpTo={(h) => setNewTimeSetted(Number(h.start_offset))}
              sessionStart={sessionStart}
            />
          </div>
        </div>

        <AddHeadingModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onHeadingCreated={handleHeadingCreated}
        />
        <AddHeadingModal
            isOpen={isAddHeadingOpen}
            onClose={() => setIsAddHeadingOpen(false)}
            defaultStartSeconds={addHeadingDefaults.startSec}
            beforeParagraphId={addHeadingDefaults.beforeId}
            onHeadingCreated={handleHeadingCreated}
        />
        {readOnly && (
          <div
            role="status"
            className="mx-3 mt-1 px-3 py-2 rounded-md bg-warning-50 text-sm text-warning-700 border border-warning-200"
          >
            {lockReason === "reloading"
              ? "Die Transkription dieser Sitzung wurde erneuert. Die Ansicht wird aktualisiert …"
              : "Diese Sitzung wird gerade neu transkribiert. Änderungen sind erst danach wieder möglich; die Ansicht aktualisiert sich dann von selbst."}
          </div>
        )}
        {/* Content Area with optional Nav */}
        <div className="flex flex-1 min-h-0 relative">
          {/* Navigation box (sections and speakers) */}
          {Array.isArray(localNavData) && localNavData.length > 0 && (
            <div className={readOnly ? "contents pointer-events-none" : "contents"} {...lockedProps}>
            <NavigationBox
              key={`nav-${navRefresh ?? 0}`}
              navData={localNavData}
              sessionName={sessionName}
              filtersExpanded={filtersExpanded}
              onToggleFilters={onToggleFilters}
              filterSpeakers={filterSpeakers}
              selectedFilterSpeakers={selectedFilterSpeakers}
              onFilterSpeakersChange={onFilterSpeakersChange}
              headingOptions={headingOptions}
              selectedHeadingFilters={selectedHeadingFilters}
              onHeadingFilterChange={onHeadingFilterChange}
              selectedGroupByKeys={selectedGroupByKeys}
              onGroupByChange={onGroupByChange}
              onToggleSpeakersModal={onToggleSpeakersModal}
              onToggleAllSpeakers={onToggleAllSpeakers}
              areAllSpeakersOpen={areAllSpeakersOpen}
              sessionId={sessionId}
              sessionStart={sessionStart}
              speakers={speakers}
              getRefIndexByParagraphId={(id) => paragraphIdToRefIndex.get(id)}
              onJumpToRefIndex={(refIdx) => {
                // Update audio current time based on the paragraph start
                const targetPara = paragraphChildren?.[Math.max(0, refIdx - 1)];
                if (targetPara?.start != null) {
                  setNewTimeSetted(targetPara.start);
                }
                handleScrollToNested(0, refIdx);
              }}
              activeParagraphId={activeParagraphId}
              getHeadingStartByBeforeId={getHeadingStartByBeforeId}
              getParagraphStartById={(id) => paragraphIdToStart.get(id) ?? null}
              onEditHeading={(headingObj) => {
                setSelectedHeadingForEdit(headingObj);
                setIsEditHeadingOpen(true);
              }}
              onAddHeading={handleOpenAddHeading}
              onDeleteHeading={handleDeleteHeading}
              onCommitReorder={handleCommitReorder}
              onMergeSelected={async (ids, speakerKey) => {
                try {
                  if (!Array.isArray(ids) || ids.length < 2) return;
                  // Map selected IDs to paragraphs in current flat order
                  const idToIndex = new Map();
                  paragraphChildren.forEach((p, idx) => { if (p?.id != null) idToIndex.set(p.id, idx); });
                  const selected = ids
                    .map((pid) => ({ idx: idToIndex.get(pid), p: paragraphChildren[idToIndex.get(pid)] }))
                    .filter((x) => x.idx != null && x.p);
                  // Ensure contiguous by index
                  selected.sort((a, b) => a.idx - b.idx);
                  if (selected.length < 2) return;
                  for (let i = 1; i < selected.length; i++) {
                    if (selected[i].idx !== selected[i - 1].idx + 1) {
                      console.warn("Non-contiguous selection; merge aborted");
                      return;
                    }
                  }
                  const first = selected[0].p;
                  const last = selected[selected.length - 1].p;
                  // Snapshot before anything is written: the surviving row is
                  // edited and the rest are deleted, so undo needs all of them
                  // in full, plus any heading merge is about to re-anchor.
                  // Canonical rows from the server: the view normalises
                  // `start` to a number, which must not be written back.
                  const undoBefore = await fetchSnapshots(selected.map(({ p }) => p.id));
                  const undoDeletedIds = new Set(selected.slice(1).map(({ p }) => p.id));
                  const undoHeadings = [];
                  (Array.isArray(localNavData) ? localNavData : []).forEach((section) => {
                    const candidates = [section?.heading2, ...(section?.children || [])];
                    candidates.forEach((h) => {
                      if (h && h.type !== "paragraph" && h.id != null &&
                          undoDeletedIds.has(Number(h.before_paragraph_id))) {
                        undoHeadings.push({ id: h.id, before_paragraph_id: h.before_paragraph_id });
                      }
                    });
                  });
                  // Build merged contents
                  const mergedRaw = selected.map(({ p }) => p.raw_content || p.original_content || "").join("\n").trim();
                  const mergedOriginal = selected.map(({ p }) => p.original_content || "").join("\n\n").trim();
                  const mergedCorrected = selected.map(({ p }) => p.corrected_content || p.original_content || "").join("\n\n").trim();
                  // Compute new end duration for the first paragraph
                  const firstStart = Number(first.start) || 0;
                  const lastStart = Number(last.start) || 0;
                  const lastDur = isoDurationToSeconds(last.end);
                  const newTotalDuration = Math.max(lastStart + lastDur - firstStart, 0);
                  const newEndIso = secondsToIsoDuration(newTotalDuration);
                  // Extract chosen speaker
                  let speakerName = null;
                  let speakerNumber = undefined;
                  if (typeof speakerKey === "string") {
                    const [namePart, numPart] = speakerKey.split("::");
                    speakerName = namePart || null;
                    if (numPart && numPart.trim() !== "") {
                      const parsed = Number(numPart);
                      if (Number.isFinite(parsed)) speakerNumber = parsed;
                    }
                  }
                  // Update first paragraph
                  await dispatch(editParagraphAsync({
                    id: first.id,
                    payload: {
                      raw_content: mergedRaw,
                      original_content: mergedOriginal,
                      corrected_content: mergedCorrected,
                      end: newEndIso,
                      ...(speakerName ? { speaker_name: speakerName } : {}),
                      ...(speakerNumber !== undefined ? { speaker_number: speakerNumber } : {}),
                    }
                  }));
                  // Delete the rest
                  for (let i = 1; i < selected.length; i++) {
                    await dispatch(deleteParagraphAsync(selected[i].p.id));
                  }
                  // Update headings whose before_paragraph_id pointed to any deleted paragraph
                  const deletedIds = selected.slice(1).map(({ p }) => p.id);
                  const deletedIdSet = new Set(deletedIds);
                  if (Array.isArray(localNavData) && deletedIds.length > 0) {
                    const affected = [];
                    for (const section of localNavData) {
                      const h = section?.heading2;
                      if (h && deletedIds.includes(Number(h.before_paragraph_id))) {
                        affected.push(h);
                      }
                      for (const child of section?.children || []) {
                        if (child?.type !== "paragraph") {
                          const hh = child;
                          if (hh && deletedIds.includes(Number(hh.before_paragraph_id))) {
                            affected.push(hh);
                          }
                        }
                      }
                    }
                    const isoStartForFirst = secondsToCustomDateTime(sessionStart, Number(first.start) || 0);
                    for (const heading of affected) {
                      await dispatch(editHeadingAsync({
                        id: heading.id,
                        payload: {
                          before_paragraph_id: first.id,
                          start: isoStartForFirst
                        }
                      }));
                      // Optimistic local updates for nav headings and waveform regions
                      setLocalNavData((prev) =>
                        (prev || []).map((sec) => {
                          const updateH = (h) =>
                            h && h.id === heading.id
                              ? { ...h, before_paragraph_id: first.id, start: Number(first.start) || h.start }
                              : h;
                          return {
                            ...sec,
                            heading2: updateH(sec.heading2),
                            children: (sec.children || []).map((c) =>
                              c?.type === "paragraph" ? c : updateH(c)
                            ),
                          };
                        })
                      );
                      setWaveHeadings((prev) => {
                        const list = Array.isArray(prev) ? prev.slice() : [];
                        const idx = list.findIndex((h) => h.id === heading.id);
                        if (idx !== -1) list[idx] = { ...list[idx], start: Number(first.start) || list[idx].start };
                        return list;
                      });
                    }
                  }
                  // Record the inverse now that every write has landed.
                  if (undoBefore.length) dispatch(pushUndo(buildUndoEntry({
                    type: "merge",
                    label: `Zusammenführen von ${undoBefore.length} Absätzen`,
                    sessionId,
                    before: undoBefore,
                    after: [{
                      ...first,
                      raw_content: mergedRaw,
                      original_content: mergedOriginal,
                      corrected_content: mergedCorrected,
                      end: newEndIso,
                      ...(speakerName ? { speaker_name: speakerName } : {}),
                      ...(speakerNumber !== undefined ? { speaker_number: speakerNumber } : {}),
                    }],
                    headingsBefore: undoHeadings,
                  })));
                  // Optimistic updates to localData (main content) and localNavData (navigation)
                  setLocalData((prev) => {
                    if (!Array.isArray(prev)) return prev;
                    const updatedFirst = {
                      ...first,
                      raw_content: mergedRaw,
                      original_content: mergedOriginal,
                      corrected_content: mergedCorrected,
                      end: newEndIso,
                      ...(speakerName ? { speaker_name: speakerName } : {}),
                      ...(speakerNumber !== undefined ? { speaker_number: speakerNumber } : {}),
                    };
                    const firstId = first.id;
                    return prev.map((section) => {
                      const children = Array.isArray(section?.children) ? section.children : [];
                      let hasChanges = false;
                      const newChildren = children.reduce((acc, child) => {
                        if (child?.type === "paragraph") {
                          if (deletedIdSet.has(child.id)) {
                            hasChanges = true;
                            return acc;
                          }
                          if (child.id === firstId) {
                            hasChanges = true;
                            acc.push({ ...child, ...updatedFirst });
                            return acc;
                          }
                        }
                        acc.push(child);
                        return acc;
                      }, []);
                      return hasChanges ? { ...section, children: newChildren } : section;
                    });
                  });
                  setLocalNavData((prev) => {
                    const next = (prev || []).map((sec) => {
                      const newChildren = (sec.children || [])
                        .map((c) =>
                          c?.type === "paragraph" && c.id === first.id
                            ? {
                                ...c,
                                raw_content: mergedRaw,
                                original_content: mergedOriginal,
                                corrected_content: mergedCorrected,
                                end: newEndIso,
                                ...(speakerName ? { speaker_name: speakerName } : {}),
                                ...(speakerNumber !== undefined ? { speaker_number: speakerNumber } : {}),
                              }
                            : c
                        )
                        .filter((c) => !(c?.type === "paragraph" && deletedIdSet.has(c.id)));
                      return { ...sec, children: newChildren };
                    });
                    return next;
                  });

                  // Re-read from the server. The optimistic updates above
                  // cover the text, but `paragraph_words` is distributed to
                  // paragraphs by TIME RANGE when the transcript loads — the
                  // merged paragraph is still carrying only the first half's
                  // words, so text that came from the second half has no DB
                  // word behind it and cannot be marked or highlighted until
                  // a reload. Refetching re-runs the distribution.
                  if (sessionId) {
                    await dispatch(getFullTranscriptionByIdAsync(sessionId));
                  }
                } catch (e) {
                  console.error("Merge failed", e);
                }
              }}
              onInlineHeadingRename={handleQuickHeadingRename}
            />
            </div>
          )}
          {/* Scrollable Section */}
          <div ref={scrollContainerRef} className={`flex-1 overflow-y-auto ${isEmbedded ? "h-full" : "h-[86vh]"} mt-1 px-3`}>
        {/* Inside the scroll container, so a locked transcript still scrolls. */}
        <div className={readOnly ? "opacity-60 select-none pointer-events-none" : undefined} {...lockedProps}>
        {groupedSections.map((section, i) => (
          
          
        <NestedSection
            key={i}
            ref={sectionRefs.current[i]}
            sectionIndex={i}
            section={section}
            isActive={selectedSectionMap === `section-${i}`}
            targetNested={selectedSectionMap === `section-${i}` ? targetNested : null}
            selectedSectionKey={openSectionKey}
            setSelectedSectionKey={setOpenSectionKey}
            selectedNestedSectionMap={selectedNestedSectionMap}
            selectedSectionMap={selectedSectionMap}
            currentTime={currentTime}
            isPlaying={isPlaying}
            setCurrentTime={setCurrentTime}
            sectionTime={section.heading2.start}
            setNewTimeSetted={setNewTimeSetted}
            correctedParagraphs={correctedParagraphs}
            setCorrectedParagraphs={setCorrectedParagraphs}
            sessionId={sessionId}
            sessionStart={sessionStart}
            setIsAddSectionModalOpen={setIsModalOpen}
            setSelectedSpeakers={setSelectedSpeakers}
            allSections={groupedSections}
            speakers={speakers}
            openAllNestedToken={expandAllToken}
            expandAll={expandAll}
            promptMode={promptMode}
            onOpenSpeakersModal={onToggleSpeakersModal}
          />
        ))}
        </div>

          

          </div>
        </div>
        {/* Edit Heading Modal */}
        {isEditHeadingOpen && selectedHeadingForEdit && (
          <EditHeadingModal
            heading={selectedHeadingForEdit}
            isOpen={isEditHeadingOpen}
            onClose={() => setIsEditHeadingOpen(false)}
            sessionId={sessionId}
          />
        )}

      </div>
      </HighlightsProvider>
    );
};

// Thin wrapper around AudioPlayer that pulls highlights from the context
// and forwards them as a prop. Kept local to SessionView so the player
// itself stays unaware of the context (easier to reuse elsewhere).
const HighlightAwareAudioPlayer = ({ onHighlightJumpTo, ...rest }) => {
  const { highlights } = useHighlights();
  return (
    <AudioPlayer
      {...rest}
      highlights={highlights}
      onHighlightClick={onHighlightJumpTo}
    />
  );
};

export default SessionView;
